import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { getStoragePath, getFirestorePath, hasStorageAccess } from '../../utils/vaultUtils';
import { ACCESS_LEVELS } from '../../utils/accessControl';
import { v4 as uuidv4 } from 'uuid';
import logger from '../../utils/logger';

const UploadDialog = ({
  open,
  onClose,
  selectedFiles = [],
  currentPath = '',
  collectionType = 'documents',
  storageArea = ACCESS_LEVELS.PERSONAL,
}) => {
  const { currentUser } = useAuth();
  const storage = getStorage();
  const db = getFirestore();
  
  // Normalize storage area
  const normalizedStorageArea = ['documents', 'photos', 'records'].includes(storageArea) ? 
    ACCESS_LEVELS.VAULT : storageArea;
  
  // State for upload progress and errors
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [visibility] = useState('private');
  
  // Check if user has access to the selected storage area
  const hasAccess = hasStorageAccess(currentUser, normalizedStorageArea);
  
  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFiles.length || !hasAccess) return;
    
    setUploading(true);
    setUploadProgress({});
    setUploadErrors({});
    
    try {
      const uploadPromises = selectedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const fileId = uuidv4();
          
          // Get storage path using utility function
          const storagePath = getStoragePath({
            userId: currentUser.uid,
            familyCode: currentUser.familyCode,
            collectionType,
            fileId,
            storageArea: normalizedStorageArea
          });
          
          // Create storage reference
          const storageRef = ref(storage, storagePath);
          
          // Upload file to storage
          const uploadTask = uploadBytesResumable(storageRef, file);
          
          // Track upload progress
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: progress,
              }));
            },
            (error) => {
              logger.error('Storage upload error:', error);
              setUploadErrors((prev) => ({
                ...prev,
                [file.name]: error.message,
              }));
              reject(error);
            },
            async () => {
              try {
                // Get download URL
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                // Create document data
                const docData = {
                  id: fileId,
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  path: currentPath,
                  url: downloadURL,
                  createdAt: new Date(),
                  createdBy: currentUser.uid,
                  userId: currentUser.uid, // Required for security rules
                  storageArea: normalizedStorageArea,
                  visibility: visibility
                };
                
                // Add family code for family documents
                if (normalizedStorageArea === ACCESS_LEVELS.FAMILY) {
                  docData.familyCode = currentUser.familyCode;
                  docData.isPersonal = false;
                }
                
                // Add vault type for vault documents
                if (normalizedStorageArea === ACCESS_LEVELS.VAULT) {
                  docData.vaultType = collectionType;
                  docData.familyCode = currentUser.familyCode;
                  docData.isPersonal = false;
                }
                
                // Add personal flag for personal documents
                if (normalizedStorageArea === ACCESS_LEVELS.PERSONAL) {
                  docData.isPersonal = true;
                }

                // Get Firestore path using utility function
                const collectionPath = getFirestorePath({
                  userId: currentUser.uid,
                  familyCode: currentUser.familyCode,
                  collectionType,
                  storageArea: normalizedStorageArea
                });
                
                
                // Add the document to Firestore
                await addDoc(collection(db, collectionPath), docData);
                resolve();
              } catch (error) {
                logger.error('Error saving document metadata:', error);
                let errorMessage = error.message;
                
                // Provide more user-friendly error messages
                if (error.code === 'permission-denied') {
                  errorMessage = 'You do not have permission to save this file.';
                }
                
                setUploadErrors((prev) => ({
                  ...prev,
                  [file.name]: errorMessage,
                }));
                reject(error);
              }
            }
          );
        });
      });

      await Promise.all(uploadPromises);
      onClose();
    } catch (error) {
      logger.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (fileName) => {
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="upload-dialog"
      data-collection-type={collectionType}
      data-storage-area={storageArea}
    >
      <DialogTitle>
        Upload Files
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {Object.entries(uploadErrors).map(([fileName, error]) => (
          <Typography key={fileName} color="error" variant="caption" component="div">
            Error uploading {fileName}: {error}
          </Typography>
        ))}
        <List>
          {selectedFiles && selectedFiles.map((file) => (
            <ListItem key={file.name}>
              <ListItemText
                primary={file.name}
                secondary={
                  uploadProgress[file.name] !== undefined ? (
                    <Box component="span" sx={{ width: '100%', mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress[file.name]}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary" component="span">
                        {Math.round(uploadProgress[file.name])}%
                      </Typography>
                    </Box>
                  ) : null
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="remove"
                  onClick={() => handleRemoveFile(file.name)}
                  disabled={uploading}
                >
                  <CloseIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          color="primary"
          disabled={uploading || selectedFiles.length === 0}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
